/**
 * GPS Prediction Service for FlowRide
 * Implements dead reckoning for position prediction in free and constrained modes
 * Subscribes to FilteredGpsDataService and emits predicted positions
 */

import {
    RawGpsData,
    RouteSegment,
} from "../../types/Navigation";

/**
 * Service that predicts GPS position using dead reckoning
 */
export class GpsPredictionService {

    /** Singleton instance */
    private static instance: GpsPredictionService | null = null;

    /** Route segments for constrained mode */
    private routeSegments: RouteSegment[] = [];

    /** Current segment index in constrained mode */
    private currentSegmentIndex: number = 0;

    /** Whether the service is active */
    private active: boolean = false;

    /** Last value for prediction in navigation mode for smoothing */
    private lastValue: RawGpsData | null = null;

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
        // Private constructor
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): GpsPredictionService {
        if ( GpsPredictionService.instance === null ) {
            GpsPredictionService.instance = new GpsPredictionService();
        }

        return GpsPredictionService.instance;
    }

    /**
     * Set route segments for constrained mode
     * @param segments - Array of route segments
     */
    public setRouteSegments( segments: RouteSegment[] ): void {
        this.routeSegments = segments;
        this.currentSegmentIndex = 0;

        // console.log( `[GpsPredictionService] Route segments updated: ${ segments.length } segments` );
    }

    /**
     * Clear route segments
     */
    public clearRouteSegments(): void {
        this.routeSegments = [];
        this.currentSegmentIndex = 0;

        // console.log( "[GpsPredictionService] Route segments cleared" );
    }

    /**
     * Get current route segments
     * @returns Array of route segments
     */
    public getRouteSegments(): RouteSegment[] {
        return [ ...this.routeSegments ]; // Return a copy to prevent external modification
    }

    // Simulation methods removed - no longer needed

    /**
     * Start prediction service
     */
    public start(): void {
        if ( this.active ) {
            console.warn( "[GpsPredictionService] Already active - skipping start" );

            return;
        }

        this.active = true;

        // Subscribe to filtered GPS data
    }

    /**
     * Stop prediction service
     */
    public stop(): void {
        this.active = false;
    }

    public getLastValue(): RawGpsData | null {
        return this.lastValue;
    }

    /**
     * Calculate current predicted position at the moment of the call
     * @returns Current predicted GPS data
     */
    public predict( dataPoint: RawGpsData | null = null, elapsed: number | null = null ): RawGpsData {
        if ( dataPoint === null ) {
            if ( this.lastValue === null ) {
                throw Error( "[GpsPredictionService] Data Point not initialized, can not compute a prediction..." );
            }
            dataPoint = this.lastValue;
        }

        if ( dataPoint.speed === 0.0 ) {
            return dataPoint;
        }

        if ( elapsed === null && this.lastValue !== null ) {
            elapsed = Date.now() - this.lastValue.timestamp;
        } else if ( elapsed === null && this.lastValue === null ) {
            throw Error( "[GpsPredictionService] can not predict data on empty elapsed time and data" );
        }

        // distance is expressed in km (speed m/s -> km/h *= 3.6, time ms => h /= 3.6e+6)
        const distance = dataPoint.speed * elapsed! / 1000;
        console.log( `[GpsPredictionService] Elapsed: ${ elapsed! / 1000 }s, Distance : ${ distance }km` );

        // const LatLng = this.moveAlongSegment( dataPoint.latitude, dataPoint.longitude,  this.routeSegments[this.currentSegmentIndex], distance );
        const LatLng = this.moveAlongSegment( dataPoint.latitude, dataPoint.longitude, this.routeSegments[this.currentSegmentIndex], distance );
        this.updateCurrentSegment( LatLng.latitude, LatLng.longitude );


        return {
            ...dataPoint,
            "timestamp": Date.now(),
            "latitude":  LatLng.latitude,
            "longitude": LatLng.longitude,
        } as RawGpsData;
    }

    /**
     * Move position by distance and bearing
     * @param lat - Starting latitude
     * @param lon - Starting longitude
     * @param bearing - Bearing in degrees
     * @param distance - Distance in meters
     * @returns New position
     */
    private movePosition(
        lat: number,
        lon: number,
        bearing: number,
        distance: number,
    ): {
        "latitude":  number;
        "longitude": number;
    } {
        const R = 6371000; // Earth's radius in meters
        const δ = distance / R; // Angular distance
        const θ = ( bearing * Math.PI ) / 180; // Bearing in radians

        const φ1 = ( lat * Math.PI ) / 180;
        const λ1 = ( lon * Math.PI ) / 180;

        const φ2 = Math.asin( Math.sin( φ1 ) * Math.cos( δ ) + Math.cos( φ1 ) * Math.sin( δ ) * Math.cos( θ ) );

        const λ2 =
            λ1 +
            Math.atan2(
                Math.sin( θ ) * Math.sin( δ ) * Math.cos( φ1 ),
                Math.cos( δ ) - Math.sin( φ1 ) * Math.sin( φ2 ),
            );

        return {
            "latitude":  ( φ2 * 180 ) / Math.PI,
            "longitude": ( λ2 * 180 ) / Math.PI,
        };
    }

    /**
     * Snap position to nearest point on segment
     * @param lat - Latitude
     * @param lon - Longitude
     * @param segment - Route segment
     * @returns Snapped position
     */
    private snapToSegment(
        lat: number,
        lon: number,
        segment: RouteSegment,
    ): {
        "latitude":  number;
        "longitude": number;
    } {
        const [ lon1, lat1 ] = segment.start;
        const [ lon2, lat2 ] = segment.end;

        // Calculate projection of point onto line segment
        const dx = lon2 - lon1;
        const dy = lat2 - lat1;

        const t = Math.max(
            0,
            Math.min( 1, ( ( lon - lon1 ) * dx + ( lat - lat1 ) * dy ) / ( dx * dx + dy * dy ) ),
        );

        return {
            "latitude":  lat1 + t * dy,
            "longitude": lon1 + t * dx,
        };
    }

    /**
     * Move along segment by distance
     * @param lat - Starting latitude
     * @param lon - Starting longitude
     * @param segment - Route segment
     * @param distance - Distance to move in meters
     * @returns New position
     */
    private moveAlongSegment(
        lat: number,
        lon: number,
        segment: RouteSegment,
        distance: number,
    ): {
        "latitude":  number;
        "longitude": number;
    } {
        // Simply move in the direction of the segment bearing
        return this.movePosition( lat, lon, segment.bearing, distance );
    }

    /**
     * Update current segment based on position
     * @param lat - Latitude
     * @param lon - Longitude
     */
    private updateCurrentSegment( lat: number, lon: number ): void {
        let minDistance = Infinity;
        let nearestIndex = this.currentSegmentIndex;

        // Check current and nearby segments
        const searchRange = 3;
        const start = Math.max( 0, this.currentSegmentIndex - searchRange );
        const end = Math.min( this.routeSegments.length, this.currentSegmentIndex + searchRange + 1 );

        for ( let i = start; i < end; i++ ) {
            const segment = this.routeSegments[i];
            if ( segment === undefined ) {
                continue;
            }

            const distance = this.distanceToSegment( lat, lon, segment );
            if ( distance < minDistance ) {
                minDistance = distance;
                nearestIndex = i;
            }
        }

        if ( nearestIndex !== this.currentSegmentIndex ) {
            this.currentSegmentIndex = nearestIndex;

            // console.log( `[GpsPredictionService] Current segment updated to: ${ nearestIndex }` );
        }
    }

    /**
     * Calculate distance from point to segment
     * @param lat - Latitude
     * @param lon - Longitude
     * @param segment - Route segment
     * @returns Distance in meters
     */
    private distanceToSegment( lat: number, lon: number, segment: RouteSegment ): number {
        const snapped = this.snapToSegment( lat, lon, segment );

        return this.calculateDistance( lat, lon, snapped.latitude, snapped.longitude );
    }

    /**
     * Calculate distance between two points
     * @param lat1 - Latitude of first point
     * @param lon1 - Longitude of first point
     * @param lat2 - Latitude of second point
     * @param lon2 - Longitude of second point
     * @returns Distance in meters
     */
    private calculateDistance( lat1: number, lon1: number, lat2: number, lon2: number ): number {
        const R = 6371000;
        const φ1 = ( lat1 * Math.PI ) / 180;
        const φ2 = ( lat2 * Math.PI ) / 180;
        const Δφ = ( ( lat2 - lat1 ) * Math.PI ) / 180;
        const Δλ = ( ( lon2 - lon1 ) * Math.PI ) / 180;

        const a =
            Math.sin( Δφ / 2 ) * Math.sin( Δφ / 2 ) +
            Math.cos( φ1 ) * Math.cos( φ2 ) * Math.sin( Δλ / 2 ) * Math.sin( Δλ / 2 );

        const c = 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );

        return R * c;
    }

    /**
     * Check if service is active
     * @returns True if active
     */
    public isActive(): boolean {
        return this.active;
    }

}

/**
 * Export singleton instance for convenience
 */
export const gpsPredictionService = GpsPredictionService.getInstance();
